import { useCallback, useEffect, useMemo, useState } from 'react';
import { isHttpError, useHttp, useHttpQuery } from '@mono/http-hook';
import { useCurrentOrganization } from '../organizations/hooks';
import { useParams } from 'react-router';

export type ProjectSummary = {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  organizationId: string;
  collaboratorsCount: number;
  createdAt: string;
  updatedAt: string;
  role: string;
};

type ProjectsResponse = {
  projects: ProjectSummary[];
};

const getHttpErrorMessage = (error: unknown, fallback: string) => {
  if (isHttpError(error)) {
    const responseData = error.response?.data;
    if (responseData && typeof responseData === 'object') {
      const { error: errorMessage, message: responseMessage } =
        responseData as {
          error?: string;
          message?: string;
        };
      return errorMessage ?? responseMessage ?? error.message ?? fallback;
    }
    return error.message ?? fallback;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return fallback;
};

const sortProjects = (projects: ProjectSummary[]) =>
  [...projects].sort((a, b) => a.name.localeCompare(b.name));

type CreateProjectInput = {
  name: string;
  slug?: string;
  description?: string;
};

type CreateProjectResponse = {
  project: ProjectSummary;
};

export const useProjects = () => {
  const http = useHttp();
  const currentOrganization = useCurrentOrganization();
  const organizationId = currentOrganization?.id ?? '';

  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(Boolean(organizationId));
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const fetchProjects = useCallback(async () => {
    if (!organizationId) {
      setProjects([]);
      setIsLoading(false);
      setLoadError(null);
      return;
    }

    setIsLoading(true);
    setLoadError(null);

    try {
      const { data } = await http.get<ProjectsResponse>(
        `/api/v1/organizations/${organizationId}/projects`,
        {
          withCredentials: true,
        }
      );

      setProjects(sortProjects(data.projects));
    } catch (error) {
      setProjects([]);
      setLoadError(
        getHttpErrorMessage(error, 'Unable to load projects right now.')
      );
    } finally {
      setIsLoading(false);
    }
  }, [http, organizationId]);

  useEffect(() => {
    void fetchProjects();
  }, [fetchProjects]);

  const metrics = useMemo(() => {
    const totalProjects = projects.length;
    const totalCollaborators = projects.reduce(
      (total, project) => total + project.collaboratorsCount,
      0
    );

    const latestUpdate = projects.reduce<Date | null>((latest, project) => {
      const date = new Date(project.updatedAt);
      if (Number.isNaN(date.getTime())) {
        return latest;
      }
      if (!latest) return date;
      return date.getTime() > latest.getTime() ? date : latest;
    }, null);

    return {
      totalProjects,
      totalCollaborators,
      latestUpdate,
    };
  }, [projects]);

  const createProject = useCallback(
    async (input: CreateProjectInput) => {
      if (!organizationId) {
        throw new Error('Organization is not selected.');
      }

      setIsCreating(true);
      try {
        const { data } = await http.post<CreateProjectResponse>(
          `/api/v1/organizations/${organizationId}/projects`,
          input,
          {
            withCredentials: true,
          }
        );

        setProjects((prev) => sortProjects([...prev, data.project]));
        return data.project;
      } catch (error) {
        throw new Error(
          getHttpErrorMessage(error, 'Unable to create project right now.')
        );
      } finally {
        setIsCreating(false);
      }
    },
    [http, organizationId]
  );

  return {
    projects,
    isLoading,
    loadError,
    refresh: fetchProjects,
    metrics,
    createProject,
    isCreating,
  };
};

export const useCurrentProject = () => {
  const currentOrganization = useCurrentOrganization();
  const params = useParams<{ tab: string; projectId: string }>();
  return useHttpQuery<{project: ProjectSummary}>({
    queryKey: ['current-project', params.projectId, currentOrganization?.id],
    config: {
      method: 'get',
      url: `/api/v1/organizations/${currentOrganization?.id}/projects/${params.projectId}`,
    },
  });
};
